/**
 * useUsage - Hook to fetch tenant usage statistics
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface UsageData {
  records: {
    used: number;
    max: number;
    remaining: number;
    percent: number;
  };
  sites: {
    used: number;
    max: number;
    remaining: number;
    percent: number;
  };
  status: 'ok' | 'warning' | 'critical' | 'exceeded';
  plan: string;
  summary: string;
  upgradePrompt: {
    message: string;
    nextPlan: string | null;
    url: string;
  } | null;
}

async function fetchUsage(): Promise<UsageData> {
  return api.get<UsageData>('/api/v1/usage');
}

export function useUsage() {
  return useQuery({
    queryKey: ['usage'],
    queryFn: fetchUsage,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

/**
 * Check if sync is allowed based on current usage
 * Note: Only RECORD limits block sync. Site limits only block adding new sites.
 */
export function canSync(usage: UsageData | undefined): { allowed: boolean; message?: string } {
  if (!usage) {
    return { allowed: true }; // Allow by default if no data
  }

  // Only record limits block sync - site limits don't affect syncing existing sites
  if (usage.records.percent >= 100) {
    return {
      allowed: false,
      message: `Record limit reached (${usage.records.used.toLocaleString()}/${usage.records.max.toLocaleString()}). Upgrade to sync more.`,
    };
  }

  if (usage.records.percent >= 95) {
    return {
      allowed: true,
      message: `Almost at record limit (${usage.records.percent}% used). Consider upgrading.`,
    };
  }

  if (usage.records.percent >= 80) {
    return {
      allowed: true,
      message: `Approaching record limit (${usage.records.percent}% used).`,
    };
  }

  return { allowed: true };
}
